/*
  This code defines a JavaScript component for managing a quick add-to-cart drawer, providing various states and actions depending on product availability and other conditions.

  - quickAddDrawer(): A factory function that returns an object representing the quick add drawer component. It includes properties:
    - email: Initially empty, intended to default to the logged-in user's email.
    - success: Tracks the success state of the interaction, initially null.
    - state: Computes the current state of the drawer based on various conditions like product availability.
    - buttonLabel: Determines the label on the button based on the current state.
    - drawerTitle: Provides the title of the drawer.
    - showEmailInput: Determines whether to show an input for the email based on the product's state.

  - Methods:
    - init(): Initializes the component by setting it as a global variable and setting up selected options.
    - handleFormSubmit(): Handles form submission for coming soon and back-in-stock notifications.
    - handleBottomButtonClick(): Manages actions to be taken when the bottom button is clicked, such as adding to cart or closing the drawer.

  - Properties:
    - klaviyoComingSoonListIdNormalized: Normalizes the ID used for Klaviyo list subscriptions.
  
  The component is registered with Alpine.js to be used in the HTML as a dynamic component, enhancing user interaction with product availability notifications and quick purchase options.
*/

const quickAddDrawer = () => ({

  email: '',
  success: null,

  get state() {
    if (this.success) {
      return 'success';
    }

    return this.stateBase;
  },

  get buttonLabel() {
    const { state } = this;
    if (state === 'success') return this.success.buttonLabel;
    return this.buttonLabelBase;
  },

  get drawerTitle() {
    return 'Quick Add';
  },

  get showEmailInput() {
    const { state } = this;
    return state === 'coming-soon' || state === 'sold-out-notify';
  },

  init() {
    window.quickAddDrawer = this;

    if (this.quickAddDrawerSelectedOptions) {
      Object.assign(this.selectedOptions, this.quickAddDrawerSelectedOptions);
      this.normalizeOptionsAndVariants(Object.entries(this.selectedOptions));
    }

    // don't show drawer if there are no options to select
    if (this.visibleOptions.length === 0 && this.state === 'available') {
      this.handleBottomButtonClick();
      this.$el.closest('dialog').close();
      return;
    }

    this.$dispatch('update-quick-add-drawer-title', this.drawerTitle);

    this.$watch('drawerTitle', (newTitle, oldTitle) => {
      if (newTitle === oldTitle) return;
      this.$dispatch('update-quick-add-drawer-title', newTitle);
    });
  },

  get klaviyoComingSoonListIdNormalized() {
    return this.product.comingSoonListId?.value || this.klaviyoComingSoonListId;
  },

  async handleFormSubmit() {
    if (this.isWorking) return;

    let requestTypeUrl;
    let requestData;
    if (this.isComingSoon) {
      requestTypeUrl = 'subscriptions';
      requestData = {
        type: 'subscription',
        attributes: {
          profile: {
            data: { type: 'profile', attributes: { email: this.email } },
          },
        },
        relationships: {
          list: {
            data: { type: 'list', id: this.klaviyoComingSoonListIdNormalized },
          },
        },
      };
    } else {
      const { selectedVariants } = this;

      if (selectedVariants.length !== 1) {
        alert(`Expected exactly 1 variant, got ${selectedVariants.length}`);
        return;
      }

      const [selectedVariant] = selectedVariants;

      let variantId = selectedVariant.id.replace('gid://shopify/ProductVariant/', '');

      requestTypeUrl = 'back-in-stock-subscriptions';
      requestData = {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: {
            data: { type: 'profile', attributes: { email: this.email } },
          },
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${variantId}`,
            },
          },
        },
      };
    }

    const url = `https://a.klaviyo.com/client/${requestTypeUrl}/?company_id=${this.klaviyoCompanyId}`;
    const options = {
      method: 'POST',
      headers: { revision: '2023-07-15', 'content-type': 'application/json' },
      body: JSON.stringify({ data: requestData }),
    };

    try {
      this.isWorking = true;
      const res = await fetch(url, options);

      if (!res.ok) {
        let description = '';
        try {
          const data = await res.json();
          description = data.errors.map((err) => err.detail).join('\n');
        } catch (e) {
          //
        }

        throw new Error(
          ['Failed to subscribe to list!', description, `(status: ${res.status})`].filter(Boolean).join('\n'),
        );
      }

      this.success = PRODUCT_STATES_CONFIG[this.state].success;

      setTimeout(() => {
        window.quickAddDrawer?.$refs.successTitle?.focus();
      }, 0);
    } catch (error) {
      alert(error.message);
    } finally {
      this.isWorking = false;
    }
  },

  async handleBottomButtonClick() {
    if (this.isWorking) return;

    const { state } = this;

    if (state === 'available') {
      await this.addToCart();
      this.$el.closest('dialog').close();
    } else if (state === 'success') {
      this.$el.closest('dialog').close();
    }
  },
});

document.addEventListener('alpine:init', () => {
  Alpine.data('quickAddDrawer', quickAddDrawer);
});
